/**
 * teacherVerification — de knop achter migratie 0020.
 *
 * `verify_teacher()` en `list_pending_teachers()` staan sinds 0020 in de
 * database, admin-only, en werden door **niets** aangeroepen. Daardoor was
 * verificatie een eenrichtingsdeur: `teacher_status` staat standaard op
 * `pending` — óók voor bestaande leerkrachten — en `is_teacher_of()` houdt een
 * niet-geverifieerde leerkracht bij élke leerling weg. Zonder aanroeper kon
 * niemand die deur openen behalve via de SQL-console.
 *
 * Dat is het derde geval in dit project van "de beveiliging staat er, het
 * bedieningspaneel niet". Zie ook `regenerate_link_code` (0013, drie maanden
 * zonder aanroeper) en `request_data_access` (0008).
 *
 * DE AUTORISATIE ZIT IN DE DATABASE, NIET HIER.
 * `verify_teacher()` controleert zelf `is_admin()` en weigert anders. Deze
 * module voegt geen enkele controle toe — dat zou een tweede plek zijn waar de
 * regel staat, en de zwakste van de twee wint dan bij de volgende wijziging.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
export interface PendingTeacher {
  id: string;
  displayName: string | null;
  /** De school: de registernaam bij een keuze, de aanvraagtekst bij een aanvraag. */
  schoolName: string | null;
  /**
   * Waar die naam vandaan komt (0041). Dit is het gegeven waarop verifiëren
   * rust: `registry` is natrekbaar, `request` is een bewering, `none` betekent
   * geen schoolbinding.
   */
  schoolSource: 'registry' | 'request' | 'none' | null;
  createdAt: string | null;
}
export interface VerifyResult {
  ok: boolean;
  message?: string;
}

/**
 * Leerkrachten die op verificatie wachten.
 *
 * `schoolName` was hier lang altijd `null`: de kolom bestond niet en het
 * registratiescherm stuurde de naam niet mee. 0038 dichtte dat, en 0041 voegde
 * `schoolSource` toe — het onderscheid tussen een school die uit ons register is
 * GEKOZEN en een naam die de aanvrager zelf opschreef. Zonder dat onderscheid is
 * verifiëren een handeling die eruitziet als een controle.
 * Vastgelegd in `docs/specs/TEACHER_VERIFICATION.md`.
 */
export async function listPendingTeachers(): Promise<{
  ok: boolean;
  teachers: PendingTeacher[];
  message?: string;
}> {
  const {
    data,
    error
  } = await supabase.rpc('list_pending_teachers');
  if (error) {
    logError('[TeacherVerification] Wachtlijst ophalen mislukt', error);
    return {
      ok: false,
      teachers: [],
      message: 'De wachtlijst kon niet worden opgehaald.'
    };
  }
  const rows = (data ?? []) as Array<{
    id: string;
    display_name: string | null;
    school_name: string | null;
    school_source?: 'registry' | 'request' | 'none' | null;
    created_at: string | null;
  }>;
  return {
    ok: true,
    teachers: rows.map(r => ({
      id: r.id,
      displayName: r.display_name,
      schoolName: r.school_name,
      // Ontbreekt het veld, dan draait 0041 nog niet: dan is de herkomst
      // onbekend en niet 'request'. Een gok zou een gekozen school onterecht
      // als bewering presenteren.
      schoolSource: r.school_source ?? null,
      createdAt: r.created_at
    }))
  };
}

/**
 * Keurt een leerkracht goed of af.
 *
 * Beide richtingen lopen via dezelfde RPC, omdat beide hetzelfde spoor moeten
 * achterlaten (`teacher_verified_by`, `teacher_verified_at`). Afwijzen stil
 * afhandelen zou betekenen dat er geen enkel bewijs is dat er een afweging is
 * gemaakt — en juist bij een afwijzing is dat het bewijs dat je later nodig
 * hebt.
 */
export async function verifyTeacher(teacherId: string, approve: boolean): Promise<VerifyResult> {
  const {
    error
  } = await supabase.rpc('verify_teacher', {
    p_teacher_id: teacherId,
    p_approve: approve
  });
  if (error) {
    logError('[TeacherVerification] Verifiëren mislukt', error);
    return {
      ok: false,
      message: approve ? 'Deze leerkracht is niet geverifieerd. Er is niets gewijzigd.' : 'De afwijzing is niet doorgevoerd. Er is niets gewijzigd.'
    };
  }
  return {
    ok: true
  };
}